import { consoleLogger } from '../constants/console-logger';
import { DeviceType } from '../device-api/device-type';
const getUserAgent = (context) => context.navigator.userAgent;
const getPlatform = (context) => context.navigator.platform;
const getMaxTouchPoints = (context = window) => context.navigator.maxTouchPoints || 0;
const matchUserAgent = (regex, context = window) => regex.test(getUserAgent(context));
const matchPlatform = (regex, context = window) => regex.test(getPlatform(context));
export const isIOS = () => matchPlatform(/iPhone|iPod/i) || isIpad() || matchUserAgent(/iPhone|iPod/i);
export const isIpad = () => matchPlatform(/iPad/i) || (matchPlatform(/MacIntel/i) && getMaxTouchPoints() > 1) || matchUserAgent(/iPad/i);
export const isTablet = () => matchUserAgent(/Tablet(?!\sPC)/i) || isIpad();
export const isWindowsPhone = () => matchUserAgent(/Windows\sPhone/i);
export const isBlackBerry = () => matchUserAgent(/BlackBerry|BB|PlayBook/i);
export const isKindle = () => matchUserAgent(/Kindle/i);
export const isOculus = () => matchUserAgent(/OculusBrowser/i);
export const isAmazon = () => isAndroid() && matchUserAgent(/Silk/i);
export const isHuawei = () => isAndroid() && matchUserAgent(/Huawei/i);
export const isChromeBook = () => matchUserAgent(/\bCrOS\b/i);
export const isWindows = () => matchUserAgent(/Win/i) || matchPlatform(/Win/i);
export const isLinux = () => matchPlatform(/Linux|HP-UX|FreeBSD|OpenBSD|SunOS/i);
export const isApple = () => isIOS() || isMac();
export const isSupportedMobileDevices = () => isAndroid() || isIOS() || isAmazon() || isHuawei();
export const isMobileOrTablet = () => !isOculus() &&
    (isAndroid() || isIOS() || isTablet() || isWindowsPhone() || isWebOs() || isBlackBerry() || isKindle() || isAmazon() || isHuawei());
export const isTouchDevice = () => 'ontouchstart' in window || getMaxTouchPoints() > 0;
export const isMac = () => matchPlatform(/Mac/i);
export const isIon = (context) => Boolean(context.ion);
export const isVizio = (context) => matchUserAgent(/vizio|conjure|smartcast/i, context);
export const isChromecast = (context) => matchUserAgent(/crkey/i, context);
// Conflicting definition of Android and AndroidTV
export const isAndroid = () => matchUserAgent(/Android/i);
const isAndroidTV = (context) => Object.prototype.hasOwnProperty.call(context, 'Android');
// conflicting definition of webOs
export const isWebOs = () => matchUserAgent(/webOS|hpwOS/i);
const isWeb0s = (context) => matchUserAgent(/web0s/i, context);
const isVidaa = (context) => matchUserAgent(/hisense|vidaa/i, context);
const isWebmafOrProspero = (context) => {
    if (matchUserAgent(/playstation 3/i, context))
        return DeviceType.WebMaf;
    if (matchUserAgent(/playstation 4/i, context)) {
        const webMafVersionStr = getUserAgent(context).match(/webmaf\/v([0-9]+)(.*)/i)?.[1];
        if (!webMafVersionStr) {
            consoleLogger.info("Can't lookup WebMafVersion, will fallback to WebMaf");
            return DeviceType.WebMaf;
        }
        const webmafVersion = parseInt(webMafVersionStr, 10);
        return webmafVersion >= 3 ? DeviceType.Prospero : DeviceType.WebMaf;
    }
    if (matchUserAgent(/playstation 5/i, context))
        return DeviceType.Prospero;
    return false;
};
const isTizen = (context) => Object.prototype.hasOwnProperty.call(context, 'tizen');
const isYouView = (context) => matchUserAgent(/youviewhtml/i, context);
const isUWP = (context) => matchUserAgent(/msapphost/i, context);
const isXboxSeries = (context) => {
    const deviceForm = context.Windows.System.Profile.AnalyticsInfo.deviceForm;
    return Boolean(deviceForm.toLowerCase().includes('xbox series'));
};
const isNetgem = (context) => matchUserAgent(/FVP_STB/i, context);
const isLlama = (context) => matchUserAgent(/sky_|xioneuk/i, context);
const isOneSdk = (context) => matchUserAgent(/EOSSTB|EOS2STB|APLSTB|APPSTB/i, context);
const isMultiChoiceRdk = (context) => matchUserAgent(/MDMP1001S|PS5525IMC/i, context);
const isXfinity = (context) => matchUserAgent(/xfinity| wpe/i, context);
const isZenterio = (context) => matchUserAgent(/dt_stb_bcm/i, context);
const isQuest = (context) => matchUserAgent(/OculusBrowser/i, context);
const isKepler = (context) => matchUserAgent(/AFTCA002/i, context);
const isKpn = (context) => /deviceModel=KPN/i.test(context.location.search);
const isWinUI2 = (context) => matchUserAgent(/winui2/, context);
export const identifyDeviceType = (context = self) => {
    if (isIon(context))
        return DeviceType.Ion;
    // This rule needs to take precedence over chromecast because the vizio agent string can also includes 'chromecast'
    if (isVizio(context))
        return DeviceType.Vizio;
    // This rule needs to take precedence over the android one because the chromecast agent string can also include an 'android' word
    if (isChromecast(context))
        return DeviceType.Chromecast;
    if (isVidaa(context))
        return DeviceType.Vidaa;
    const playstationDeviceType = isWebmafOrProspero(context);
    if (playstationDeviceType)
        return playstationDeviceType;
    if (isWeb0s(context))
        return DeviceType.WebOS;
    if (isTizen(context))
        return DeviceType.Tizen;
    if (isYouView(context))
        return DeviceType.YouView;
    if (isAndroidTV(context))
        return DeviceType.Android;
    if (isUWP(context)) {
        return isXboxSeries(context) ? DeviceType.XboxSeries : DeviceType.XboxOne;
    }
    if (isNetgem(context))
        return DeviceType.Netgem;
    // Must be above Xfinity due to containing 'WPE'
    if (isLlama(context))
        return DeviceType.Llama;
    if (isOneSdk(context))
        return DeviceType.OneSdk;
    if (isMultiChoiceRdk(context))
        return DeviceType.MultiChoiceRdk;
    if (isXfinity(context))
        return DeviceType.Xfinity;
    if (isZenterio(context))
        return DeviceType.Zenterio;
    if (isQuest(context))
        return DeviceType.Quest;
    if (isKepler(context))
        return DeviceType.Kepler;
    if (isKpn(context))
        return DeviceType.Kpn;
    if (isWinUI2(context))
        return DeviceType.WinUI2;
    return DeviceType.Browser;
};
//# sourceMappingURL=device-identifier.js.map